import { BADGES, BADGE_GROUPS, BADGE_MAP } from "@/lib/achievements";
import { Lock } from "lucide-react";

// Compact row of earned badges (icon pills)
export function BadgeRow({ earned = [], max = 4 }) {
  const list = (earned || []).map((k) => BADGE_MAP[k]).filter(Boolean);
  if (list.length === 0) return null;
  const shown = list.slice(0, max);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="badge-row">
      {shown.map(({ key, label, Icon }) => (
        <span key={key} title={BADGE_MAP[key].desc} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent/10 text-accent text-xs font-medium" data-testid={`badge-${key}`}>
          <Icon className="h-3.5 w-3.5" /> {label}
        </span>
      ))}
      {list.length > max && <span className="px-2 py-0.5 rounded-full bg-secondary text-xs">+{list.length - max}</span>}
    </div>
  );
}

// Grouped achievements: Verification · Booking Milestones · Performance.
// Earned badges lead each group; locked ones are clearly marked, never broken-looking.
export function AchievementsGrid({ earned = [], showLocked = true }) {
  const set = new Set(earned || []);
  return (
    <div className="space-y-6" data-testid="achievements-grid">
      {BADGE_GROUPS.map((group) => {
        const inGroup = BADGES.filter((b) => b.group === group.key);
        const items = showLocked ? [...inGroup].sort((a, b) => (set.has(b.key) ? 1 : 0) - (set.has(a.key) ? 1 : 0))
          : inGroup.filter((b) => set.has(b.key));
        if (items.length === 0) return null;
        const earnedCount = inGroup.filter((b) => set.has(b.key)).length;
        return (
          <div key={group.key} data-testid={`badge-group-${group.key}`}>
            <div className="flex items-baseline gap-2">
              <h3 className="text-sm font-semibold">{group.label}</h3>
              <span className="text-xs text-muted-foreground">{earnedCount} of {inGroup.length} earned</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-3">
              {items.map(({ key, label, desc, Icon }) => {
                const on = set.has(key);
                return (
                  <div key={key}
                    className={`flex items-start gap-3 rounded-xl border p-3.5 transition-colors ${on ? "border-accent/40 bg-accent/5" : "border-dashed border-border bg-secondary/30"}`}
                    data-testid={`achievement-${key}${on ? "-earned" : "-locked"}`}>
                    <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${on ? "bg-accent/10" : "bg-background"}`}>
                      {on ? <Icon className="h-5 w-5 text-accent" /> : <Lock className="h-4 w-4 text-muted-foreground" />}
                    </span>
                    <div className="min-w-0">
                      <p className={`font-medium text-sm leading-tight ${on ? "" : "text-muted-foreground"}`}>{label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{on ? desc : `Not yet earned · ${desc}`}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
